/**
 * Public metadata for the workspace this tab is in.
 *
 * Fetched unauthenticated, because a login page has to render before anyone has
 * signed in. The endpoint returns only what is already visible to whoever can
 * load the URL — name, slug, branding, signup policy — and never a roster, staff
 * list or count.
 */
import { useQuery } from '@tanstack/react-query';
import type { TenantPublicDTO } from '@cwy/config/contracts';
import { apiGet } from '@/lib/api';
import { tenant } from '@/lib/tenant';

export function useTenantInfo() {
  return useQuery<TenantPublicDTO | null>({
    queryKey: ['tenant', tenant.slug],
    // At the root domain there is no school to look up — the first-party tenant
    // needs no branding and its login page is the default one.
    enabled: Boolean(tenant.slug),
    queryFn: () => apiGet<TenantPublicDTO>(`/tenant?slug=${encodeURIComponent(tenant.slug!)}`),
    // A school's name and policy change rarely; refetching on every mount would
    // add a round trip to the login screen for nothing.
    staleTime: 5 * 60_000,
    retry: false,
  });
}
